'use client';
import { useFormatter, useTranslations } from 'next-intl';
import { type FC, useEffect } from 'react';
import { BaseLineCoupon } from '@/app/components/icons/BaseLineCoupon';
import { SITE } from '@/app/constants';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { couponsListMyCoupons } from '../apis';

const COUPON_BANNER_BG_STYLE: React.CSSProperties = {
  backgroundImage:
    SITE === 'china'
      ? 'url(https://image01.vidu.zone/vidu/media-asset/coupon-c4900450.png)'
      : 'url(https://image01.cf.vidu.studio/vidu/media-asset/coupon-c4900450.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

export const CouponBanner: FC = () => {
  const t = useTranslations();
  const format = useFormatter();
  const { couponSummary, updateCouponSummary } = useStore((state) => ({
    couponSummary: state.couponSummary,
    updateCouponSummary: state.updateCouponSummary,
  }));
  const { open: openCouponDialog } = useGlobalModal('couponDialog');

  const { run: runCouponList } = useFetch(couponsListMyCoupons, {
    manual: true,
    onSuccess: ({ data }) => {
      updateCouponSummary({
        unusedCount: data?.unused_count ?? 0,
        nearestUnusedExpireAt: data?.nearest_unused_expire_at ?? null,
      });
    },
  });

  useEffect(() => {
    // couponSummary 为空时拉取（首次进入，或支付关闭后被重置 → 重新同步可用券数量）
    if (!couponSummary) {
      runCouponList({ query: { 'pager.pagesz': 1, 'pager.page': 0 } });
    }
  }, [couponSummary]);

  const count = couponSummary?.unusedCount ?? 0;
  const nearestExpireAt = couponSummary?.nearestUnusedExpireAt ?? null;

  const description =
    count > 0 && nearestExpireAt
      ? t('payments.coupon.bannerDescription', {
          count,
          date: format.dateTime(new Date(nearestExpireAt), {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        })
      : t('payments.coupon.bannerEmpty');

  const handleClick = () => {
    reportEvent('element_click', { from_page: 'my-subscription', element_name: 'coupons' });
    openCouponDialog();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      style={COUPON_BANNER_BG_STYLE}
      className="relative mt-5 flex w-full items-center justify-between overflow-hidden rounded-16 bg-[#212428] px-10 py-4 text-left shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] ring-1 ring-system-white08 sm:mx-4 sm:mt-0 sm:w-[calc(100%-32px)] sm:p-4"
    >
      <div className="relative flex min-w-0 items-center gap-4 sm:gap-2">
        <div className="flex items-center gap-2">
          <BaseLineCoupon className="size-6 shrink-0 text-white" />
          <span className="font-semibold text-white text-xl sm:text-base">{t('payments.coupon.title')}</span>
          {count > 0 && (
            <span className="flex items-center justify-center rounded-4 bg-[#5765F2] px-2 py-0.5 font-semibold text-sm text-white">
              {count}
            </span>
          )}
        </div>
        <span className="truncate text-sm text-white opacity-40 sm:hidden">{description}</span>
      </div>
      <span className="relative inline-flex shrink-0 items-center rounded-12 bg-system-white08 px-8 py-3 text-sm text-white ring-1 ring-system-white08 transition-colors hover:bg-system-white16 sm:px-4 sm:py-2">
        {t('payments.coupon.viewDetail')}
      </span>
    </button>
  );
};
